import { client as defaultApolloClient } from '../services/client';
import {
  DELETE_MEDIA_OBJECT,
  REQUEST_MEDIA_UPLOAD,
  REQUEST_MEDIA_UPLOAD_BATCH,
} from '../graphql/mutations/mediaMutations';

export const MAX_MEDIA_UPLOAD_BATCH_ITEMS = 10;
export const DEFAULT_MEDIA_UPLOAD_CONCURRENCY = 3;
const OCTET_STREAM_MIME = 'application/octet-stream';
const CONTENT_TYPE_ALIASES = {
  'image/jpg': 'image/jpeg',
  'image/pjpeg': 'image/jpeg',
  'image/x-png': 'image/png',
};
const EXTENSION_CONTENT_TYPES = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
};

export function normalizeMediaKey(value) {
  if (typeof value !== 'string') {
    return '';
  }

  let key = value.trim();
  if (!key) {
    return '';
  }

  try {
    if (/^https?:\/\//i.test(key)) {
      const url = new URL(key);
      key = url.pathname;
    }
  } catch (_error) {
    return '';
  }

  key = key.split('?')[0].split('#')[0].replace(/^\/+/, '');

  if (key.startsWith('public/')) {
    key = key.slice('public/'.length);
  }

  return key;
}

export function parseUploadFields(fields) {
  let parsedFields = fields;

  for (let depth = 0; depth < 3; depth += 1) {
    if (parsedFields && typeof parsedFields === 'object' && !Array.isArray(parsedFields)) {
      return parsedFields;
    }

    if (typeof parsedFields !== 'string') {
      break;
    }

    try {
      parsedFields = JSON.parse(parsedFields);
    } catch (_error) {
      break;
    }
  }

  throw new Error('Invalid media upload fields');
}

function getFileExtension(fileOrName) {
  const name = typeof fileOrName === 'string' ? fileOrName : fileOrName?.name;
  const match = /\.([^.?#/]+)(?:[?#].*)?$/i.exec(name || '');
  return match?.[1]?.toLowerCase() || '';
}

function canonicalizeContentType(value) {
  const contentType = (value || '').toLowerCase().trim();
  return CONTENT_TYPE_ALIASES[contentType] || contentType;
}

function getExtensionContentType(extension, mediaKind) {
  if (extension === 'webm' && mediaKind === 'AUDIO') {
    return 'audio/webm';
  }
  return EXTENSION_CONTENT_TYPES[extension] || '';
}

export function normalizeUploadContentType({ contentType, file, fileName, mediaKind } = {}) {
  const explicitContentType = canonicalizeContentType(contentType);
  if (explicitContentType && explicitContentType !== OCTET_STREAM_MIME) {
    return explicitContentType;
  }

  const fileContentType = canonicalizeContentType(file?.type);
  if (fileContentType && fileContentType !== OCTET_STREAM_MIME) {
    return fileContentType;
  }

  return getExtensionContentType(getFileExtension(fileName || file), mediaKind) || explicitContentType || fileContentType;
}

function getFetch(fetchImpl) {
  const resolvedFetch = fetchImpl || (typeof fetch === 'function' ? fetch : undefined);
  if (typeof resolvedFetch !== 'function') {
    throw new Error('fetch is not available for media upload');
  }
  return resolvedFetch;
}

function getFormData() {
  if (typeof FormData === 'undefined') {
    throw new Error('FormData is not available for media upload');
  }
  return new FormData();
}

function normalizeUploadItem(item) {
  if (!item || typeof item !== 'object') {
    throw new Error('Media upload item is required');
  }

  const file = item.file;
  if (!file) {
    throw new Error('Media upload file is required');
  }

  const contentType = normalizeUploadContentType({
    contentType: item.contentType,
    file,
    fileName: item.fileName || file.name,
    mediaKind: item.mediaKind,
  });
  const contentLength = item.contentLength ?? file.size;

  if (!contentType) {
    throw new Error('Media upload contentType is required');
  }
  if (!Number.isFinite(Number(contentLength)) || Number(contentLength) < 1) {
    throw new Error('Media upload contentLength is required');
  }

  return {
    file,
    input: {
      entityType: item.entityType,
      entityId: item.entityId,
      parentEntityId: item.parentEntityId,
      mediaKind: item.mediaKind,
      fileName: item.fileName || file.name,
      contentType,
      contentLength: Number(contentLength),
      slot: item.slot,
    },
  };
}

function removeEmptyInputFields(input) {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
}

async function postToPresignedUrl(grant, file, fetchImpl) {
  if (!grant?.uploadUrl) {
    throw new Error('Missing media upload URL');
  }

  const fields = parseUploadFields(grant.fields);
  const formData = getFormData();

  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  let response;
  try {
    response = await getFetch(fetchImpl)(grant.uploadUrl, {
      method: 'POST',
      body: formData,
    });
  } catch (error) {

    throw error;
  }

  if (!response?.ok) {
    throw new Error(`Media upload failed with status ${response?.status || 'unknown'}`);
  }

  return response;
}

async function runWithConcurrency(items, concurrency, worker) {
  const results = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from(
    { length: Math.min(Math.max(1, concurrency), items.length) },
    async () => {
      while (nextIndex < items.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        results[currentIndex] = await worker(items[currentIndex], currentIndex);
      }
    }
  );

  await Promise.all(workers);
  return results;
}

export async function uploadOwnedMedia(options) {
  const apolloClient = options?.apolloClient || defaultApolloClient;
  const fetchImpl = options?.fetchImpl;
  const item = normalizeUploadItem(options);
  const uploadInput = removeEmptyInputFields(item.input);

  let data;
  try {
    const result = await apolloClient.mutate({
      mutation: REQUEST_MEDIA_UPLOAD,
      variables: {
        input: uploadInput,
      },
    });
    data = result.data;
  } catch (error) {
    throw error;
  }

  const grant = data?.requestMediaUpload;
  if (!grant) {
    throw new Error('Media upload grant was not returned');
  }

  await postToPresignedUrl(grant, item.file, fetchImpl);

  return {
    key: normalizeMediaKey(grant.key),
    s3Key: grant.s3Key,
    grant,
  };
}

export async function uploadOwnedMediaBatch(options = {}) {
  const apolloClient = options.apolloClient || defaultApolloClient;
  const fetchImpl = options.fetchImpl;
  const concurrency = options.concurrency || DEFAULT_MEDIA_UPLOAD_CONCURRENCY;
  const items = (options.items || []).map(normalizeUploadItem);

  if (items.length < 1) {
    return [];
  }
  if (items.length > MAX_MEDIA_UPLOAD_BATCH_ITEMS) {
    throw new Error(`Media upload batch is limited to ${MAX_MEDIA_UPLOAD_BATCH_ITEMS} files`);
  }

  const { data } = await apolloClient.mutate({
    mutation: REQUEST_MEDIA_UPLOAD_BATCH,
    variables: {
      input: {
        items: items.map((item) => removeEmptyInputFields(item.input)),
      },
    },
  });

  const grants = data?.requestMediaUploadBatch;
  if (!Array.isArray(grants) || grants.length !== items.length) {
    throw new Error('Media upload batch grants were not returned');
  }

  return runWithConcurrency(items, concurrency, async (item, index) => {
    const grant = grants[index];
    await postToPresignedUrl(grant, item.file, fetchImpl);
    return {
      key: normalizeMediaKey(grant.key),
      s3Key: grant.s3Key,
      grant,
    };
  });
}

export async function deleteOwnedMedia(options) {
  if (!options || typeof options !== 'object') {
    throw new Error('Media delete options are required');
  }

  const apolloClient = options.apolloClient || defaultApolloClient;
  const key = normalizeMediaKey(options.key);

  if (!key) {
    throw new Error('Media key is required');
  }

  const { data } = await apolloClient.mutate({
    mutation: DELETE_MEDIA_OBJECT,
    variables: {
      input: removeEmptyInputFields({
        key,
        entityType: options.entityType,
        entityId: options.entityId,
        parentEntityId: options.parentEntityId,
        mediaKind: options.mediaKind,
      }),
    },
  });

  return data?.deleteMediaObject;
}
